import { buildAnnotationClassNames } from "./annotationClassColors.js";
import type { ImageMeasurementModelKind } from "./constants/systemTraits.js";
import { keypointSchemaForAxes } from "./types/measurementKeypointPlacement.js";

export type AnnotationModelEntryType = "imageMeasurement" | "standCountMeasurement";

/** CaptureMetadata category fields that also define a COCO category. */
export interface AnnotationCategorySchema {
  name: string;
  supercategory?: string;
  keypointNames?: string[];
  keypointSkeleton?: number[][];
}

type CategorySource = "configuredPrompts" | readonly AnnotationCategorySchema[];

interface ModelCategoryDefinition {
  imageMeasurement: CategorySource;
  standCountMeasurement: CategorySource;
}

function measurementCategory(name: string, axes: readonly string[]): AnnotationCategorySchema {
  const schema = keypointSchemaForAxes(axes);
  if (!schema) {
    throw new Error(`Measurement category ${name} must define at least one keypoint axis.`);
  }
  return { name, ...schema };
}

function plantMeasurementCategory(): AnnotationCategorySchema {
  return measurementCategory("plant", ["length", "width"]);
}

const STANDARD_MEASUREMENT_AXES = ["length", "width"];
const BISECTED_LETTUCE_AXES = ["length", "width", "core_length"];
const PLANT_CENTER_CATEGORY: AnnotationCategorySchema = {
  name: "plant",
  keypointNames: ["plant_center"],
};

/**
 * Closed-set category vocabulary for every image-measurement model kind.
 *
 * Closed-set stand-count producers intentionally store the crop-neutral `plant`
 * category. Open-set configs remain prompt-driven because relabel traits use those
 * prompts as their editable class vocabulary; persisted producer-only categories are
 * appended by the COCO exporter. `none` has no model-owned vocabulary, so the editor
 * continues to discover manual category names from persisted annotations.
 *
 * Strawberry is the one COCO-specific schema: the exporter normalizes the model's
 * length/width landmarks to the semantic top/bottom/left/right convention.
 */
const MODEL_CATEGORY_DEFINITIONS: Record<ImageMeasurementModelKind, ModelCategoryDefinition> = {
  openSetObjectDetector: {
    imageMeasurement: "configuredPrompts",
    standCountMeasurement: "configuredPrompts",
  },
  strawberry: {
    imageMeasurement: [
      {
        name: "strawberry",
        keypointNames: ["top", "bottom", "left", "right"],
        keypointSkeleton: [[0, 1], [2, 3]],
      },
    ],
    standCountMeasurement: [plantMeasurementCategory()],
  },
  apple: {
    imageMeasurement: [measurementCategory("apple", STANDARD_MEASUREMENT_AXES)],
    standCountMeasurement: [plantMeasurementCategory()],
  },
  tomato: {
    imageMeasurement: [measurementCategory("tomato", STANDARD_MEASUREMENT_AXES)],
    standCountMeasurement: [plantMeasurementCategory()],
  },
  blueberry: {
    imageMeasurement: [measurementCategory("blueberry", STANDARD_MEASUREMENT_AXES)],
    standCountMeasurement: [plantMeasurementCategory()],
  },
  spinachKeypoint: {
    imageMeasurement: [{ name: "spinach", keypointNames: ["plant_center"] }],
    standCountMeasurement: [PLANT_CENTER_CATEGORY],
  },
  arugulaKeypoint: {
    imageMeasurement: [{ name: "arugula", keypointNames: ["plant_center"] }],
    standCountMeasurement: [PLANT_CENTER_CATEGORY],
  },
  bisectedLettuce: {
    imageMeasurement: [measurementCategory("lettuce", BISECTED_LETTUCE_AXES)],
    standCountMeasurement: [measurementCategory("plant", BISECTED_LETTUCE_AXES)],
  },
  squash: {
    imageMeasurement: [measurementCategory("squash", STANDARD_MEASUREMENT_AXES)],
    standCountMeasurement: [plantMeasurementCategory()],
  },
  cucumber: {
    imageMeasurement: [measurementCategory("cucumber", STANDARD_MEASUREMENT_AXES)],
    standCountMeasurement: [plantMeasurementCategory()],
  },
  none: {
    imageMeasurement: [],
    standCountMeasurement: [],
  },
};

function cloneCategorySchema(schema: AnnotationCategorySchema): AnnotationCategorySchema {
  return {
    ...schema,
    ...(schema.keypointNames ? { keypointNames: [...schema.keypointNames] } : {}),
    ...(schema.keypointSkeleton
      ? { keypointSkeleton: schema.keypointSkeleton.map((pair) => [...pair]) }
      : {}),
  };
}

export function getAnnotationCategorySchemasForModel(
  kind: ImageMeasurementModelKind,
  entryType: AnnotationModelEntryType,
  prompts: readonly string[] = []
): AnnotationCategorySchema[] {
  const definition = MODEL_CATEGORY_DEFINITIONS[kind];
  if (!definition) return [];

  const source = definition[entryType];
  if (source === "configuredPrompts") {
    return buildAnnotationClassNames(prompts).map((name) => ({ name }));
  }
  return source.map(cloneCategorySchema);
}
